import { useEffect, useRef } from "react";
import { capture } from "@/lib/analytics";

// One shared "page load" timestamp per full page load (not per hook
// instance) — lets every section on the page report a comparable
// "how long after landing did the visitor reach this section" figure.
const PAGE_LOADED_AT = typeof window !== "undefined" ? Date.now() : null;

/**
 * Generic IntersectionObserver-backed visibility tracker. Fires
 * `enterEvent` ONCE the first time the attached element crosses
 * `threshold` visibility, and `exitEvent` (dwell time only, no PII)
 * every time it leaves after having been visible for more than
 * `minDwellMs` — short flicks during fast scrolling are not reported,
 * avoiding noise. Returns a ref to attach to the tracked element.
 */
const useVisibilityTracking = (
  id,
  { enterEvent, exitEvent, extraProps = {}, threshold = 0.4, minDwellMs = 300 } = {},
) => {
  const ref = useRef(null);
  const enteredOnceRef = useRef(false);
  const visibleSinceRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined" || !id) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const pagePath = window.location.pathname;
        if (entry.isIntersecting) {
          visibleSinceRef.current = Date.now();
          if (!enteredOnceRef.current && enterEvent) {
            enteredOnceRef.current = true;
            capture(enterEvent, {
              id,
              page_path: pagePath,
              time_to_view_ms: PAGE_LOADED_AT ? Date.now() - PAGE_LOADED_AT : null,
              ...extraProps,
            });
          }
        } else if (visibleSinceRef.current) {
          const dwellMs = Date.now() - visibleSinceRef.current;
          visibleSinceRef.current = null;
          if (dwellMs >= minDwellMs && exitEvent) {
            capture(exitEvent, { id, page_path: pagePath, dwell_ms: dwellMs, ...extraProps });
          }
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, enterEvent, exitEvent, threshold, minDwellMs]);

  return ref;
};

/**
 * Attach to any landing/marketing page `<section>` — fires
 * `section_viewed` once (how far down the page the visitor actually
 * reached) and `section_time_spent` every time it leaves the
 * viewport (how long they dwelled there). `sectionId` should be a
 * stable identifier (reuse the section's existing `data-testid`).
 */
export const useSectionTracking = (sectionId) =>
  useVisibilityTracking(sectionId, { enterEvent: "section_viewed", exitEvent: "section_time_spent" });

/**
 * Attach to a CTA button/link — fires `cta_viewed` once the CTA
 * actually scrolls into view (distinct from `cta_click` in
 * analytics.js, which only fires on an actual click). No exit/dwell
 * event — a CTA's dwell time isn't a meaningful signal the way a
 * content section's is.
 */
export const useCtaVisibility = (ctaId, extraProps) =>
  useVisibilityTracking(ctaId, { enterEvent: "cta_viewed", extraProps, threshold: 0.6 });
